import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * WHAT EACH WRITE INVALIDATES — declared once, in one table.
 *
 * Longer cache lifetimes are only safe if every write reliably knocks out
 * everything it could have changed. Before this file that knowledge was spread
 * across eight mutation handlers as eight hand-maintained lists, and auditing
 * them turned up real gaps — a recorded payment did not refresh the dashboard
 * or the notification tray, and cancelling a booking left a PAYMENT_DUE alert
 * in the bell for a booking that no longer owed anything.
 *
 * The rule this table encodes:
 *
 *   INVALIDATE BY WHAT THE SERVER CHANGED, NOT BY WHAT THE SCREEN SHOWS.
 *
 * A payment does not merely change "payments". It can flip the booking to
 * CONFIRMED, raise a voucher, raise an invoice, move the receivable on the
 * dashboard and in reports, and silence a due-balance notification — all
 * inside one server transaction. The screen you happen to be standing on when
 * you record it is irrelevant to which of those became wrong.
 *
 * Over-invalidating costs a refetch. Under-invalidating shows a stale
 * receivable after a refund. Where the two trade off, this table always errs
 * toward the refetch.
 *
 * Invalidation is by key ROOT (`['payments']` matches `['payments','ledger',id]`
 * and everything else beneath it), which is why the query keys are consistently
 * `[domain, kind, ...id]`.
 */

/** Every cache root the app uses. */
type Root =
  | 'dashboard'
  | 'leads'
  | 'customers'
  | 'tours'
  | 'bookings'
  | 'invoices'
  | 'vouchers'
  | 'credit-notes'
  | 'payments'
  | 'reports'
  | 'settings'
  | 'shell';

/**
 * The writes this app performs, named by what HAPPENED rather than by which
 * component happened to call it.
 */
export type WriteKind =
  | 'payment.recorded'
  | 'booking.created'
  | 'booking.cancelled'
  | 'booking.deleted'
  | 'booking.statusChanged'
  | 'booking.valueRevised'
  | 'booking.edited'
  | 'booking.customerChanged'
  | 'booking.noteAdded'
  | 'document.edited'
  | 'lead.changed'
  | 'lead.converted'
  | 'customer.changed'
  | 'tour.changed'
  | 'creditNote.created';

const AFFECTS: Readonly<Record<WriteKind, readonly Root[]>> = Object.freeze({
  /**
   * The widest write in the product. One transaction can recompute `paid`,
   * auto-confirm the booking, raise the voucher (at TOKEN_PAID) or the invoice
   * (at FULLY_PAID), and move both `receivedThisMonth` and `outstanding`.
   * `shell` because a settled balance must stop generating a PAYMENT_DUE
   * notification — that alert is derived from the balance on every read, so it
   * is only as fresh as this cache entry.
   */
  'payment.recorded': ['payments', 'bookings', 'invoices', 'vouchers', 'dashboard', 'reports', 'shell'],

  /**
   * Seats are taken on the departure, so the tour catalogue's availability
   * moves; a customer may have been created inline; and a new CONFIRMED
   * booking on a near departure creates a DEPARTURE notification.
   */
  'booking.created': ['bookings', 'customers', 'tours', 'leads', 'dashboard', 'reports', 'shell'],

  /**
   * Wave 2. Releases seats, withdraws the invoice, may write a negative
   * payment or issue a credit note, and drives the receivable to zero.
   * `customers` because the customer detail screen lists this trip;
   * `shell` because both notification kinds can be derived from a booking that
   * has just stopped being live.
   */
  'booking.cancelled': [
    'bookings',
    'tours',
    'invoices',
    'payments',
    'credit-notes',
    'customers',
    'dashboard',
    'reports',
    'shell',
  ],

  /**
   * Item 1 — the booking, its passengers, payments, receipts, invoices,
   * credit notes and vouchers are all GONE, not withdrawn or settled. Wider
   * than `booking.cancelled` by exactly `vouchers`: cancelling never touches
   * a voucher, deleting erases it along with everything else.
   */
  'booking.deleted': [
    'bookings',
    'tours',
    'invoices',
    'vouchers',
    'payments',
    'credit-notes',
    'customers',
    'dashboard',
    'reports',
    'shell',
  ],

  /** Completing a trip moves staff performance and the departure board. */
  'booking.statusChanged': ['bookings', 'tours', 'customers', 'dashboard', 'reports', 'shell'],

  /**
   * `total` moves, so the receivable moves with it — and the outstanding figure
   * quoted inside a PAYMENT_DUE notification is computed from exactly that.
   */
  'booking.valueRevised': ['bookings', 'invoices', 'vouchers', 'payments', 'dashboard', 'reports', 'shell'],

  /**
   * THE WIDEST BOOKING WRITE, by design — passengers, pricing, departure (so
   * seats move on the OLD and the NEW tour) and the three overpayment
   * outcomes (refund, credit note, or nothing) can all land in the same
   * save. `shell` for the same reason `payment.recorded` carries it: a
   * settled overpayment must stop quoting a stale balance in the bell.
   */
  'booking.edited': [
    'bookings',
    'tours',
    'invoices',
    'vouchers',
    'payments',
    'credit-notes',
    'customers',
    'dashboard',
    'reports',
    'shell',
  ],

  /** Owner-only — moves which customer's lifetime value this trip counts against. */
  'booking.customerChanged': ['bookings', 'customers', 'dashboard', 'reports'],

  /**
   * Wave 1. A staff note is the NARROWEST write here: it touches no money, no
   * seat count and no derived alert. `dashboard` is included only because the
   * trip calendar is one of the two entry points that can add one.
   */
  'booking.noteAdded': ['bookings', 'dashboard'],

  /** Voucher / invoice in-place edits, including passenger corrections. */
  'document.edited': ['invoices', 'vouchers', 'bookings'],

  /** The dashboard shows recent leads and a lead count; reports show the funnel. */
  'lead.changed': ['leads', 'dashboard', 'reports'],

  'lead.converted': ['leads', 'customers', 'dashboard', 'reports'],

  'customer.changed': ['customers', 'bookings', 'leads'],

  /**
   * Tour edits, new departures, and the wave-1 soft delete. `shell` and
   * `dashboard` because the trip calendar and every DEPARTURE notification are
   * derived from `TourDate` rows, and retiring a tour cascades to them.
   */
  'tour.changed': ['tours', 'bookings', 'dashboard', 'shell'],

  /** Credit reduces what is owed against the invoice it is raised on. */
  'creditNote.created': ['credit-notes', 'invoices', 'payments', 'dashboard', 'reports'],
});

/**
 * Invalidate everything a given write could have changed.
 *
 * Returns a stable callback, so it is safe in a mutation's `onSuccess` without
 * re-creating the mutation on every render.
 */
export function useInvalidateOn(): (kind: WriteKind) => void {
  const queryClient = useQueryClient();

  return useCallback(
    (kind: WriteKind): void => {
      for (const root of AFFECTS[kind]) {
        void queryClient.invalidateQueries({ queryKey: [root] });
      }
    },
    [queryClient],
  );
}

/** Exported for the test that pins the table against the query-key inventory. */
export const INVALIDATION_TABLE = AFFECTS;
